import React from 'react';
import { Link, useLocation } from 'react-router';
import { ArrowLeft, ShoppingCart, User, MapPin, History, Home, UtensilsCrossed } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { cn } from '../../utils/cn';

interface HeaderProps {
  title?: string;
  showBack?: boolean;
  showCart?: boolean;
  showProfile?: boolean;
  transparent?: boolean;
}

const navLinks = [
  { path: '/', label: 'Accueil', icon: Home },
  { path: '/menu', label: 'Menu', icon: UtensilsCrossed },
  { path: '/localisation', label: 'Localisation', icon: MapPin },
  { path: '/commandes', label: 'Commandes', icon: History },
];

export const Header: React.FC<HeaderProps> = ({
  title,
  showBack = false,
  showCart = true,
  showProfile = true,
  transparent = false,
}) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { itemCount } = useCart();

  // const isHome = location.pathname === '/';

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-40',
        'transition-colors duration-200',
        transparent ? 'bg-transparent' : 'bg-bg-darker border-b border-gray-800'
      )}
    >
      <div className="pt-safe-top" />
      <div className="flex items-center justify-between max-w-7xl mx-auto px-4 md:px-6 lg:px-8 h-16 md:h-20">
        {/* Left: Logo */}
        <div className="flex items-center gap-8">
          {showBack ? (
            <Link
              to={-1 as any}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-bg-card/80 hover:bg-bg-card transition-colors md:hidden"
            >
              <ArrowLeft className="w-5 h-5 text-text-primary" />
            </Link>
          ) : null}

          <Link to="/" className="flex items-center">
            <div className="p-1.5 rounded-lg bg-white/30">
              <img
                src="/logo-512.png"
                alt="Ball M"
                className="h-8 md:h-10 w-auto"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Navigation principale">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path ||
                (link.path !== '/' && location.pathname.startsWith(link.path));
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
                    isActive
                      ? 'bg-primary-orange/20 text-primary-orange'
                      : 'text-text-secondary hover:text-text-primary hover:bg-bg-card'
                  )}
                >
                  <link.icon className="w-4 h-4" />
                  <span className="font-medium">{link.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Center: Title (mobile only) */}
        {title && (
          <h1 className="text-lg font-semibold text-text-primary truncate max-w-[200px] md:hidden">
            {title}
          </h1>
        )}

        {/* Right: Actions */}
        <div className="flex items-center gap-2 md:gap-3">
          {showCart && (
            <Link
              to="/panier"
              aria-label="Panier"
              className={cn(
                'relative flex items-center justify-center rounded-full transition-colors',
                'w-10 h-10 bg-bg-card/80 hover:bg-bg-card',
                'md:w-auto md:h-auto md:px-4 md:py-2 md:gap-2 md:bg-primary-orange md:hover:bg-primary-orange/90'
              )}
            >
              <ShoppingCart className="w-5 h-5 text-text-primary md:text-white" />
              <span className="hidden md:inline font-medium text-white">Panier</span>
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 md:static md:ml-1 flex items-center justify-center min-w-[20px] h-5 px-1 text-xs font-bold text-white bg-primary-orange md:bg-white md:text-primary-orange rounded-full">
                  {itemCount > 99 ? '99+' : itemCount}
                </span>
              )}
            </Link>
          )}

          {showProfile && (
            <Link
              to={isAuthenticated ? '/profil' : '/login'}
              aria-label="Profil"
              className={cn(
                'flex items-center justify-center rounded-full transition-colors',
                'w-10 h-10 bg-bg-card/80 hover:bg-bg-card',
                'md:w-auto md:h-auto md:px-4 md:py-2 md:gap-2'
              )}
            >
              <User className="w-5 h-5 text-text-primary" />
              <span className="hidden md:inline font-medium text-text-primary">
                {isAuthenticated ? 'Profil' : 'Connexion'}
              </span>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
